import React, { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import axios from "@/features/axiosHelper";
import { selectSelectedConnection } from "@/features/connections/integrationSlice";
import { useDispatch, useSelector } from "react-redux";
import { Box, Container, Grid, TextField, Typography } from "@mui/material";
import ShepButton from "@/components/GenericComponents/ShepButton";
import { createAlert } from "@/features/homepage/alertSliceList";
import _ from "lodash";
import ShepFormField from "@/components/GenericComponents/ShepFormField";

const CounterSetup = forwardRef((props, ref) => {
  const [referenceCounter, setReferenceCounter] = useState([]);
  const [oldReferenceCounter, setOldReferenceCounter] = useState([]);
  const selectedConnection = useSelector(selectSelectedConnection);
  const [hasCounter, setHasCounter] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    getReferenceCounter();
  }, []);

  const getReferenceCounter = () => {
    axios
      .get("/connectionartifactschema/reference-counter", {
        params: {
          connectionId: selectedConnection.ConnectionId,
        },
      })
      .then((response) => {
        if (response?.data?.length > 0) {
          setReferenceCounter(response.data);
          let clone = _.cloneDeep(response.data);
          setOldReferenceCounter(clone); // Create a copy, that is needed when we update (for backend validation)
          setHasCounter(true);
        } else {
          setHasCounter(false);
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              message: "Encountered an issue while fetching Reference Counter",
              buttonLabel: "Close",
            })
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      })
      .finally(() => {
      });
  };

  const getFormData = () => {
    return {
      UpdatedReferenceCounter: referenceCounter,
      OriginalReferenceCounter: oldReferenceCounter,
    };
  };

  const validate = () => {
    if (
      referenceCounter.some(
        (x) => _.toNumber(x.MaxCount) < _.toNumber(x.NextCount)
      )
    ) {
      return "Counter Setup: Max Count value cannot be less than Next Count.";
    }
    if (
      referenceCounter.some(
        (x) => _.toNumber(x.NextCount) > _.toNumber(x.MaxCount)
      )
    ) {
      return "Counter Setup: Next Count value cannot be more than Max Count.";
    }
    return "";
  };

  const onSaveSuccess = () => {
    setOldReferenceCounter(
      referenceCounter.map((counter) => ({
        ReferenceCounterId: counter.ReferenceCounterId,
        ConnectionId: counter.ConnectionId,
        TagName: counter.TagName,
        Prefix: counter.Prefix,
        NextCount: counter.NextCount,
        Increment: counter.Increment,
        MaxCount: counter.MaxCount,
        StringLength: counter.StringLength,
      }))
    );
  };

  useImperativeHandle(ref, () => ({
    getFormData,
    validate,
    onSaveSuccess,
  }));

  const handleReset = (id) => {
    const reference = referenceCounter.map((ref) => {
      if (ref.ReferenceCounterId === id) {
        ref.Prefix = "";
        ref.NextCount = 1;
        ref.Increment = 1;
        ref.MaxCount = 999999999;
        ref.StringLength = 9;
      }
      return ref;
    });

    setReferenceCounter(reference);
  };

  const handleTextChange = (event, id) => {
    const { name, value } = event.target;

    const reference = referenceCounter.map((ref) => {
      if (ref.ReferenceCounterId === id) {
        ref[name] = value;
      }
      return ref;
    });

    setReferenceCounter(reference);
  };

  const generatePreview = (prefix, counter, totalLength, char) => {
    let prefixLength = prefix.length;
    let counterLength = counter.length;
    let preview = "";

    if (totalLength > prefixLength + counterLength) {
      preview = prefix + counter.padStart(totalLength - prefixLength, char);
    } else {
      preview = prefix + counter;
      preview = preview.slice(prefixLength + counterLength - totalLength);
    }
    return preview;
  };


  return (
    <Container>
      <Grid container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={1.5} />
        <Grid item xs={9}>
          <Typography variant="h2">Counter Setup</Typography>
          {hasCounter ? (
            <>
              {referenceCounter.map((counter) => (
                <Box key={counter.ReferenceCounterId} style={{ marginTop: "20px" }}>
                  <Typography variant="h3">{counter.TagName}</Typography>
                  <Grid
                    container
                    rowSpacing={1}
                    columnSpacing={1}
                    style={{ marginTop: "10px", marginBottom: "30px" }}
                  >
                    <Grid size={1} item>
                      <Typography>Prefix</Typography>
                      <TextField
                        name="Prefix"
                        value={counter?.Prefix ?? ""}
                        onChange={(e) =>
                          handleTextChange(e, counter.ReferenceCounterId)
                        }
                      />
                    </Grid>
                    <Grid size={1} item>
                      <Typography>Next Count</Typography>
                      <ShepFormField
                        name="NextCount"
                        type="number"
                        value={counter?.NextCount ?? ""}
                        error={
                          _.toNumber(counter?.NextCount) >
                          _.toNumber(counter?.MaxCount)
                        }
                        helperText={
                          _.toNumber(counter?.NextCount) >
                            _.toNumber(counter?.MaxCount)
                            ? "Next Count value cannot be more than Max Count"
                            : ""
                        }
                        onChange={(e) =>
                          handleTextChange(e, counter.ReferenceCounterId)
                        }
                      />
                    </Grid>
                    <Grid size={1} item>
                      <Typography>Increment</Typography>
                      <TextField
                        name="Increment"
                        type="number"
                        value={counter?.Increment ?? ""}
                        onChange={(e) =>
                          handleTextChange(e, counter.ReferenceCounterId)
                        }
                      />
                    </Grid>
                    <Grid size={1} item>
                      <Typography>Max Count</Typography>
                      <ShepFormField
                        name="MaxCount"
                        type="number"
                        value={counter?.MaxCount ?? ""}
                        error={
                          _.toNumber(counter?.MaxCount) <
                          _.toNumber(counter?.NextCount)
                        }
                        helperText={
                          _.toNumber(counter?.MaxCount) <
                            _.toNumber(counter?.NextCount)
                            ? "Max Count value cannot be less than Next Count"
                            : ""
                        }
                        onChange={(e) =>
                          handleTextChange(e, counter.ReferenceCounterId)
                        }
                      />
                    </Grid>
                    <Grid size={1} item>
                      <Typography>String Length</Typography>
                      <TextField
                        name="StringLength"
                        disabled={true}
                        type="number"
                        value={counter?.StringLength ?? ""}
                        onChange={(e) =>
                          handleTextChange(e, counter.ReferenceCounterId)
                        }
                      />
                    </Grid>
                    <Grid
                      size={1}
                      item
                      style={{ display: "flex", alignItems: "end" }}
                    >
                      <ShepButton
                        variant="secondary"
                        onClick={() => handleReset(counter.ReferenceCounterId)}
                      >
                        Reset
                      </ShepButton>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </>
          ) : (
            <> Initializing Reference Counter...</>
          )}
          <Typography variant="h3">Preview</Typography>
          {hasCounter ? (
            <>
              {referenceCounter.map((counter) => (
                <Box
                  key={counter.ReferenceCounterId}
                  style={{ marginTop: "20px", display: "flex" }}
                >
                  <Typography variant="body1">{counter.TagName}:</Typography>
                  <Typography variant="body1">
                    &nbsp;
                    {generatePreview(
                      counter.Prefix.toString(),
                      counter.NextCount.toString(),
                      counter.StringLength,
                      "0"
                    )}
                  </Typography>
                </Box>
              ))}
            </>
          ) : null}
        </Grid>
        <Grid item xs={1.5} />
      </Grid>
    </Container>
  );
});

export default CounterSetup;
